/* eslint-disable max-lines */
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { FC, useEffect, useMemo, useState } from "react";
import { FieldValues, SubmitHandler, useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import Button from "../../../components/ui/Button";
import { SelectOption } from "../../../components/ui/input/Input.types";
import SelectInput from "../../../components/ui/input/SelectInput";
import useFetch from "../../../hooks/useFetch";
import { getAllLines } from "../../../services/Lines.service";
import { getTripsByLine } from "../../../services/Trips.service";

interface SearchTripProps {
  onSubmit: SubmitHandler<FieldValues>;
  onCheckboxChange?: (isTwoWayTrip: boolean) => void;
}

interface LineItem {
  id: number;
  lineName: string;
}

interface TripItem {
  trip: {
    id: number;
    departureTimeString: string;
    arrivalTimeString: string;
  };
}
const AGE_GROUP_OPTIONS = [{ name: "I/e rritur", id: 1 }];

const SearchTrip: FC<SearchTripProps> = ({ onSubmit, onCheckboxChange }) => {
  const { register, handleSubmit, setValue } = useFormContext();
  const [searchParams] = useSearchParams();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const { response: linesResponse } = useFetch(getAllLines, {
    initRequest: true,
    params: { pageindex: 1, pageSize: 999999 }
  });

  const { response: tripsResponse, handleFunc: getTrips } = useFetch(
    getTripsByLine,
    { initRequest: false }
  );

  const getParamAsNumber = (paramName: string): number | undefined => {
    return searchParams.has(paramName)
      ? Number(searchParams.get(paramName))
      : undefined;
  };

  useEffect(() => {
    const lineId = getParamAsNumber("lineId");
    if (lineId && searchParams.has("lineName")) {
      void getTrips(lineId);
    }
  }, [searchParams.get("lineId")]);

  useEffect(() => {
    setValue("ageGroupId", 1, { shouldValidate: true });
  }, [setValue]);

  useEffect(() => {
    if (searchParams.has("lineId") && searchParams.has("tripId")) {
      const lineId = getParamAsNumber("lineId");
      const tripId = getParamAsNumber("tripId");
      if (lineId) setValue("lineId", lineId, { shouldValidate: true });
      if (tripId) setValue("tripId", tripId, { shouldValidate: true });
      setValue("isTwoWayTrip", false, { shouldValidate: true });
    }
  }, [searchParams, setValue]);

  const lineOptions = useMemo(() => {
    const options = linesResponse?.data?.result?.map((el: LineItem) => ({
      name: el.lineName,
      id: el.id
    })) || [];

    if (searchParams.has("lineName") && searchParams.has("lineId") && options.length === 0) {
      options.push({
        name: searchParams.get("lineName") || "",
        id: getParamAsNumber("lineId") || 0
      });
    }

    return options;
  }, [linesResponse, searchParams]);

  const tripOptions = useMemo(() => {
    const options = tripsResponse?.data?.map((el: TripItem) => ({
      name: `${el.trip.departureTimeString} - ${el.trip.arrivalTimeString}`,
      id: el.trip.id
    })) || [];

    if (searchParams.has("tripTime") && searchParams.has("tripId") && options.length === 0) {
      options.push({
        name: searchParams.get("tripTime") || "",
        id: getParamAsNumber("tripId") || 0
      });
    }

    return options;
  }, [tripsResponse, searchParams]);

  const toggleDropdown = (dropdown: string) => {
    setOpenDropdown((current) => current === dropdown ? null : dropdown);
  };

  const handleOptionSelect = (formField: string, option: SelectOption, callback?: () => void) => {
    setValue(formField, Number(option.id), { shouldValidate: true });
    setOpenDropdown(null);
    callback?.();
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isTwoWayTrip = event.target.checked;
    setValue("isTwoWayTrip", isTwoWayTrip, { shouldValidate: true });
    if (onCheckboxChange) {
      onCheckboxChange(isTwoWayTrip);
    }
  };

  return (
    <div className="border-b md:-mx-7 mb-6 py-6 md:p-0">
      <h6 className="mb-6 text-xl md:px-7">Cakto çmimet për linjë</h6>
      <form onSubmit={handleSubmit(onSubmit)} className="md:px-7">
        <div className="md:flex items-center mb-6 w-full">
          <div className="w-full md:flex">
            <div className="md:mr-6 md:w-1/2">
              <SelectInput
                label="Zgjedh linjën"
                placeholder="Zgjedh"
                options={lineOptions}
                selectedId={getParamAsNumber("lineId")}
                isOpen={openDropdown === "line"}
                onClick={() => toggleDropdown("line")}
                onOptionSelect={(option) => {
                  handleOptionSelect("lineId", option, () => getTrips(Number(option.id)));
                }}
                {...register("lineId")}
              />
            </div>
            <div className="flex md:w-1/2 mr-0 md:mr-6 mt-6 md:mt-0">
              <div className="mr-6 w-1/2">
                <SelectInput
                  label="Zgjedh orarin"
                  placeholder="Zgjedh"
                  options={tripOptions}
                  selectedId={getParamAsNumber("tripId")}
                  isOpen={openDropdown === "trip"}
                  onClick={() => toggleDropdown("trip")}
                  onOptionSelect={(option) => handleOptionSelect("tripId", option)}
                  {...register("tripId")}
                />
              </div>
              <div className="w-1/2">
                <SelectInput
                  label="Lloji i biletës"
                  placeholder="Zgjedh"
                  options={AGE_GROUP_OPTIONS}
                  selectedId={1}
                  isOpen={openDropdown === "ageGroup"}
                  onClick={() => toggleDropdown("ageGroup")}
                  onOptionSelect={(option) => handleOptionSelect("ageGroupId", option)}
                  {...register("ageGroupId")}
                />
              </div>
            </div>
          </div>
          <div className="md:flex h-[67px] items-end justify-between">
            <div className="flex items-center w-full md:w-36 h-11 mt-4 md:mt-0">
              <input
                type="checkbox"
                id="returnTicket"
                className="cursor-pointer text-primary bg-primary"
                {...register("isTwoWayTrip")}
                onChange={handleCheckboxChange}
              />
              <label
                htmlFor="returnTicket"
                className="ml-3 text-sm text-neutral hover:text-primary cursor-pointer select-none"
              >
                Biletë kthyese
              </label>
            </div>
              <div className="">
                <Button
                  type="submit"
                  className="w-full h-11 flex justify-center items-center"
                >
                  <MagnifyingGlassIcon className="w-5 h-5"/>
                </Button>
              </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SearchTrip;
